import axios, { type AxiosResponse } from 'axios';
import authService from './authService';
import { getApiBaseUrl } from './apiConfig';
import type { VenueRecord } from './venueApi';
import type { PerformerRecord } from './performerApi';

export type ManagedEntityType = 'venue' | 'performer' | 'service_provider';

export interface ServiceProviderRecord {
    id: string;
    managedEntityId?: string | null;
    parentEntityId?: string | null;
    name: string;
    specialty?: string | null;
    bio?: string | null;
    profilePicture?: string | null;
    socialLinks?: Record<string, string> | any[] | null;
    customLinks?: Array<Record<string, any>> | null;
    isActive?: boolean;
    userId?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

export interface ManagedEntityRecord {
    id: string;
    ownerId: string;
    type: ManagedEntityType;
    name: string;
    /** Public handle behind this workspace's Standing link (`/@slug`). Null until claimed. */
    slug?: string | null;
    plan: 'free' | 'pro' | 'business' | 'organization' | 'dev';
    subscriptionStatus?: 'trialing' | 'active' | 'past_due' | 'cancelled' | 'none';
    currentPeriodEnd?: string | null;
    trialEndsAt?: string | null;
    /** Comp-access: super-admin-granted paid plan. When set and
     *  `complimentaryUntil` is null-or-future, this overrides `plan` for
     *  feature gating. See getEffectivePlan() in PlanFeatures.ts. */
    complimentaryPlan?: 'pro' | 'business' | null;
    complimentaryUntil?: string | null;
    complimentaryReason?: string | null;
    billingSettings?: Record<string, any>;
    settings?: Record<string, any>;
    defaultPanelLoopId?: string | null;
    /** Two distinct contact targets. `publicContact*` shows on the public
     *  entity profile (assume scrape-safe only). `directContact*` shows
     *  inline on event pages to SessionMembers of shared events only.
     *  No fallback between them — blank direct fields mean no inline
     *  contact on event rows. */
    publicContactEmail?: string | null;
    publicContactPhone?: string | null;
    directContactEmail?: string | null;
    directContactPhone?: string | null;
    directContactNotes?: string | null;
    deletedAt?: string | null;
    scheduledPurgeAt?: string | null;
    createdAt: string;
    updatedAt: string;
    managedVenue?: VenueRecord[];
    managedPerformer?: PerformerRecord[];
    managedServiceProvider?: ServiceProviderRecord[];
    /** The requesting user's effective role on this workspace.
     *  'owner' for entities they own, otherwise their TeamMember role.
     *  Drives sidebar/route gating on the frontend.
     *
     *  `'event_viewer'` is a transitional value being removed in the
     *  permissions rewrite — `useTeamPermission` normalizes it to `'viewer'`
     *  until the migration runs. See `docs/plans/PERMISSIONS_AUDIT.md`. */
    myRole?: 'owner' | 'admin' | 'editor' | 'viewer' | 'event_viewer';
    /** Per-user permission overrides keyed by `TeamPermissionKey`. An
     *  explicit `true` grants regardless of role; an explicit `false` denies
     *  regardless of role. Missing keys fall through to role defaults.
     *  Empty for owners (they pass every check). */
    myPermissions?: Record<string, boolean>;
}

export interface CreateManagedEntityInput {
    type: ManagedEntityType;
    name: string;
    plan?: 'free' | 'pro' | 'organization' | 'dev';
    specialty?: string;
    billingSettings?: Record<string, any>;
    settings?: Record<string, any>;
    /** Optional initial profile data so a wizard can commit type + name +
     *  bio + visibility (+ genres for performers) in a single round-trip.
     *  Backend applies these to the child profile (Venue/Performer/SP). */
    description?: string;
    bio?: string;
    isPublic?: boolean;
    genres?: string[];
}

const API_BASE = `${getApiBaseUrl()}/api/managed-entities`;

const withAuth = () => ({ headers: authService.getAuthHeader() });

export const managedEntityApi = {
    async list(params: {
        tenantId?: string;
        managedEntityId?: string;
        term?: string;
        limit?: number;
        offset?: number;
        scope?: 'global' | 'network' | 'local';
        /** Filter by deletion state. Defaults to 'active' (omits soft-deleted). */
        state?: 'active' | 'deleted' | 'all';
        timestamp?: number;
    } = {}): Promise<ManagedEntityRecord[]> {
        const res: AxiosResponse<ManagedEntityRecord[]> = await axios.get(API_BASE, {
            params,
            ...withAuth(),
        });
        return res.data;
    },

    /** Workspaces in the 30-day deletion grace period. Convenience wrapper. */
    async listDeleted(): Promise<ManagedEntityRecord[]> {
        const res: AxiosResponse<ManagedEntityRecord[]> = await axios.get(API_BASE, {
            params: { state: 'deleted' },
            ...withAuth(),
        });
        return res.data;
    },

    async get(id: string): Promise<ManagedEntityRecord> {
        const res: AxiosResponse<ManagedEntityRecord> = await axios.get(`${API_BASE}/${id}`, withAuth());
        return res.data;
    },

    async create(input: CreateManagedEntityInput): Promise<ManagedEntityRecord> {
        const res: AxiosResponse<ManagedEntityRecord> = await axios.post(API_BASE, input, withAuth());
        return res.data;
    },

    async update(id: string, patch: Partial<ManagedEntityRecord>): Promise<ManagedEntityRecord> {
        const res: AxiosResponse<ManagedEntityRecord> = await axios.put(`${API_BASE}/${id}`, patch, withAuth());
        return res.data;
    },

    async delete(id: string): Promise<{ message: string; deletedAt: string; scheduledPurgeAt: string }> {
        const res = await axios.delete(`${API_BASE}/${id}`, withAuth());
        return res.data;
    },

    async restore(id: string): Promise<ManagedEntityRecord> {
        const res: AxiosResponse<ManagedEntityRecord> = await axios.post(`${API_BASE}/${id}/restore`, {}, withAuth());
        return res.data;
    },

    async transferOwnership(id: string, newOwnerId: string): Promise<ManagedEntityRecord> {
        const res: AxiosResponse<ManagedEntityRecord> = await axios.post(`${API_BASE}/${id}/transfer`, { newOwnerId }, withAuth());
        return res.data;
    },
};

export default managedEntityApi;
